/** @flow */

import { takeLatest, put, all, call, select } from "redux-saga/effects";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import config from "@config";
import type { Action } from "redux-action";
import {
  GetReportingAlertDetailsDefaultReports,
  GetReportingAlertDetailsMyReports,
  GetReportingAlertDetailsReport,
  GetReportingAlertDetailsDefaultReport,
  GetAlertDetailsFiltersSchema,
  CreateAlertDetailsReport,
  UpdateAlertDetailsReport,
  DeleteAlertDetailsReport,
  ExportAlertDetailsReport,
  ExportAlertDetailsReportSuccess,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { getReportDataPage } from "@modules/reporting/selectors/reports/alert-details";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type ReportRequest = $Exact<{
  clientId: number,
}>;

function* onGetAlertDetailsFiltersSchema({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("AlertDetailsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAlertDetailsFiltersSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAlertDetailsFiltersSchemaError(e.response));
  }
}

function* onGetReportingAlertDetailsDefaultReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/default-reports`,
    );

    yield put(actionCreators.GetReportingAlertDetailsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingAlertDetailsDefaultReportsError(e.response));
  }
}

function* onGetReportingAlertDetailsMyReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/my-saved-reports`,
    );

    yield put(actionCreators.GetReportingAlertDetailsMyReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingAlertDetailsMyReportsError(e.response));
  }
}

type ReportDataRequest = $Exact<{
  clientId: number,
  report: Object,
  page?: number,
  noRedirect?: boolean,
}>;

function* onGetReportingAlertDetailsReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error("Invalid payload provided");
    }

    const currentPage = yield select(getReportDataPage);
    const page = (payload && payload.page) || currentPage;

    const [reportResponse, chartsReponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/alert-details`,
        payload.report.filters,
        {
          params: {
            page,
            limit: config.extendedResultsPerPage,
          },
        },
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/alert-details/charts`,
        payload.report.filters,
      ),
    ]);

    yield all([
      put(actionCreators.GetReportingAlertDetailsReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingAlertDetailsChartsSuccess(chartsReponse.data)),
    ]);

    if (!payload.noRedirect) {
      yield put(push(route("REPORTING_ALERT_DETAILS_REPORT", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield put(actionCreators.GetReportingAlertDetailsReportError(e.response));
  }
}

type ReportingAlertDetailsDefaultReportRequest = $Exact<{
  clientId: number,
  reportType: number,
  page?: number,
  noRedirect?: boolean,
}>;

function* onGetReportingAlertDetailsDefaultReport({
  payload,
}: Action<ReportingAlertDetailsDefaultReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportType) {
      throw Error("Invalid payload provided");
    }

    const currentPage = yield select(getReportDataPage);
    const page = (payload && payload.page) || currentPage;
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/default-reports`,
    );
    const { data } = response;

    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/alert-details`,
        data[payload.reportType].filters,
        {
          params: {
            page,
            limit: config.resultsPerPage,
          },
        },
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/alert-details/charts`,
        data[payload.reportType].filters,
      ),
    ]);

    yield all([
      put(actionCreators.GetReportingAlertDetailsDefaultReportsSuccess(data)),
      put(actionCreators.GetReportingAlertDetailsReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingAlertDetailsChartsSuccess(chartsResponse.data)),
      put(actionCreators.GetReportingAlertDetailsDefaultReportSuccess(data[payload.reportType])),
    ]);

    if (!payload.noRedirect) {
      yield put(push(route("REPORTING_ALERT_DETAILS_REPORT", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield all([
      put(actionCreators.GetReportingAlertDetailsReportError(e.response)),
      put(actionCreators.GetReportingAlertDetailsDefaultReportError(e.response)),
    ]);
  }
}

type CreateAlertDetailsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

export function* onCreateAlertDetailsReport({
  payload,
}: Action<CreateAlertDetailsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateAlertDetailsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingAlertDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateAlertDetailsReportError(e.response));
  }
}

type UpdateAlertDetailsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateAlertDetailsReport({
  payload,
}: Action<UpdateAlertDetailsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingAlertDetailsReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateAlertDetailsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingAlertDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAlertDetailsReportError(e.response));
  }
}

type DeleteAlertDetailsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteAlertDetailsReport({
  payload,
}: Action<DeleteAlertDetailsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteAlertDetailsReportSuccess()),
      put(actionCreators.GetReportingAlertDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAlertDetailsReportError(e.response));
  }
}

export function* onExportAlertDetailsReport({ payload }: Action<*>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters) {
    throw Error("Invalid payload provided");
  }

  try {
    const responce = yield http.post(
      `/api/v1/reports/client/${payload.clientId}/reports/alert-details/export`,
      payload.filters,
      {
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportAlertDetailsReportSuccess(responce.data));
  } catch (e) {
    yield put(actionCreators.ExportAlertDetailsReportError(e.response));
  }
}
export function* onExportAlertDetailsReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "alert-details-export.csv");
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(GetReportingAlertDetailsDefaultReports, onGetReportingAlertDetailsDefaultReports),
    takeLatest(GetReportingAlertDetailsMyReports, onGetReportingAlertDetailsMyReports),
    takeLatest(GetReportingAlertDetailsReport, onGetReportingAlertDetailsReport),
    takeLatest(GetReportingAlertDetailsDefaultReport, onGetReportingAlertDetailsDefaultReport),
    takeLatest(GetAlertDetailsFiltersSchema, onGetAlertDetailsFiltersSchema),
    takeLatest(CreateAlertDetailsReport, onCreateAlertDetailsReport),
    takeLatest(UpdateAlertDetailsReport, onUpdateAlertDetailsReport),
    takeLatest(DeleteAlertDetailsReport, onDeleteAlertDetailsReport),
    takeLatest(ExportAlertDetailsReport, onExportAlertDetailsReport),
    takeLatest(ExportAlertDetailsReportSuccess, onExportAlertDetailsReportSuccess),
  ]);
}
